// Captura de posición del cobrador para el tab Cerca: snapshot único del
// navegador bajo demanda. No se persiste, no se rastrean rutas y no hay
// watch/ubicación en vivo (no-goales del CERC-02).
import { useEffect, useRef, useState } from 'react';

export interface CercaUbicacion {
  lat: number;
  lng: number;
  precisionMetros: number | null;
}

export const CERCA_GEO_ESTADO = {
  INACTIVA: 'inactiva',
  SOLICITANDO: 'solicitando',
  CAPTURADA: 'capturada',
  DENEGADA: 'denegada',
  ERROR: 'error',
  NO_SOPORTADA: 'noSoportada',
} as const;

export type CercaGeoEstado = (typeof CERCA_GEO_ESTADO)[keyof typeof CERCA_GEO_ESTADO];

export interface UseCercaUbicacionResultado {
  estado: CercaGeoEstado;
  ubicacion: CercaUbicacion | null;
  /** Captura la posición actual una vez (o reintenta tras un fallo). */
  capturar: () => void;
}

/** Código W3C `GeolocationPositionError.PERMISSION_DENIED` (lib.dom sin `PositionError`). */
const GPS_PERMISSION_DENIED = 1;

/**
 * La opción del hook "capturada" (ubicación vigente) se conserva mientras
 * una nueva captura está en vuelo; el llamador decide qué renderizar.
 */
export function useCercaUbicacion(): UseCercaUbicacionResultado {
  const [estado, setEstado] = useState<CercaGeoEstado>(CERCA_GEO_ESTADO.INACTIVA);
  const [ubicacion, setUbicacion] = useState<CercaUbicacion | null>(null);
  const activa = useRef(true);

  useEffect(() => {
    activa.current = true;
    return () => {
      activa.current = false;
    };
  }, []);

  const capturar = () => {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator) || !navigator.geolocation) {
      setEstado(CERCA_GEO_ESTADO.NO_SOPORTADA);
      return;
    }
    setEstado(CERCA_GEO_ESTADO.SOLICITANDO);
    navigator.geolocation.getCurrentPosition(
      (posicion) => {
        if (!activa.current) return;
        setUbicacion({
          lat: posicion.coords.latitude,
          lng: posicion.coords.longitude,
          precisionMetros: Number.isFinite(posicion.coords.accuracy)
            ? posicion.coords.accuracy
            : null,
        });
        setEstado(CERCA_GEO_ESTADO.CAPTURADA);
      },
      (error) => {
        if (!activa.current) return;
        setEstado(
          error.code === GPS_PERMISSION_DENIED ? CERCA_GEO_ESTADO.DENEGADA : CERCA_GEO_ESTADO.ERROR,
        );
      },
      // Misma receta que el cobro/visita (PagoForm): precisión alta con
      // timeout acotado y caché corta; nunca una captura en vivo.
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 30_000 },
    );
  };

  return { estado, ubicacion, capturar };
}
